<template>
  <div v-if="imagesLoading" class="tw:flex tw:flex-row tw:gap-x-8">
    <CardLoadingSkeleton class="tw:w-[74%]" height="600px" />
    <CardLoadingSkeleton class="tw:w-[26%]" height="600px" />
  </div>

  <div v-else-if="isImagesError">
    <p class="tw:text-center tw:text-xl">
      {{ $t('onboarding.appLaunchError.header') }}
    </p>
  </div>
  <div v-else-if="images !== null" class="tw:flex tw:flex-col tw:gap-y-8 tw:md:flex-row tw:gap-x-8">
    <div class="tw:w-full tw:md:w-[74%]">
      <BaseCard>
        <div>
          <div class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:text-gray-800 tw:mb-5">
            <PropertyImagesHeader
              @toggleTipsModal="toggleTipsModal"
              @deleteImages="openDeleteModal"
              :deleteDisabled="selectedImageIds.length === 0"
              @openAddImageModal="openAddImageModal"
              :canUploadImage="canUploadImage"
            />
          </div>
          <div class="tw:mb-5">
            <button class="tw:flex tw:flex-row tw:text-sm" @click="openImageInfoModal">
              <InformationOutline class="tw:fill-none tw:stroke-gray-800 tw:h-5 tw:w-5 tw:mr-2" />
              <p class="tw:underline">{{ $t('image_order_about') }}</p>
            </button>
          </div>
          <div class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:text-gray-800" ref="imageGrid">
            <ImagesGrid
              :items="images"
              @openSetMainImageModal="openSetMainImageModal"
              @toggleDeleteModal="openDeleteModal"
              @openImageGallery="toggleImageGallery"
            />
          </div>
          <ImagesTipsModal :isOpen="tipsModalOpen" @closeModal="toggleTipsModal" />
          <ImagesGallery
            v-if="imageGalleryOpen"
            :accommodation="accommodation"
            :is-open="imageGalleryOpen"
            :images="images"
            @toggleImageGallery="toggleImageGallery"
            :openedImage="openedImage"
            @toggleDeleteModal="openDeleteModal"
            @openSetMainImageModal="openSetMainImageModal"
            :initialSelectedImage="openedImage"
          />
          <DeleteImageModal
            @deleteImages="deleteImagesFn"
            @closeModal="closeDeleteModal"
            :isOpen="deleteModalOpen"
            :selectedIds="deleteItemIds"
            :isDeleting="isDeletingImage"
          />
          <SetMainImageModal
            :isOpen="setMainImageOpen"
            :imageToSetAsMain="imageToSetAsMain"
            @closeModal="closeSetMainImageModal"
          />
          <ImageInfoModal :isOpen="isImageInfoOpen" @closeModal="closeImageInfoModal" />
          <AddNewImage :isOpen="addImageModalOpen" @closeModal="closeAddImageModal" @openModal="openAddImageModal" />
        </div>
      </BaseCard>
    </div>
    <div class="tw:w-full tw:md:w-[26%]">
      <PropertyImagesSidebar />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import PropertyImagesHeader from '@/components/propertyDetails/propertyImages/PropertyImagesHeader.vue'
import ImagesGrid from '@/components/propertyDetails/propertyImages/ImagesGrid.vue'
import ImagesTipsModal from '@/components/propertyDetails/propertyImages/ImagesTipsModal.vue'
import DeleteImageModal from '@/components/propertyDetails/propertyImages/DeleteImageModal.vue'
import ImagesGallery from '@/components/propertyDetails/propertyImages/ImagesGallery.vue'
import CardLoadingSkeleton from '@/components/loadingSkeletons/CardLoadingSkeleton.vue'
import AddNewImage from '@/components/propertyDetails/propertyImages/AddNewImage.vue'
import ImageInfoModal from '@/components/propertyDetails/propertyImages/ImageInfoModal.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import PropertyImagesSidebar from './PropertyImagesSidebar.vue'
import SetMainImageModal from './SetMainImageModal.vue'

import { useDeleteImages, useGetImages } from '@/components/propertyDetails/queries'
import { usePropertyImages } from '@/components/propertyDetails/propertyImages/usePropertyImages.ts'
import { selectedAccommodationId } from '@/layouts/queries'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useCampaign } from '@/components/rateConnect/queries'

import appAnalytics from '@/utils/tracking'

const { selectedImageIds, deleteItemIds, setDeleteItemIds, resetSelectedIds, isUploading, resetDeleteItemIds } =
  usePropertyImages()

const tipsModalOpen = ref(false)
const deleteModalOpen = ref(false)
const imageGalleryOpen = ref(false)
const addImageModalOpen = ref(false)
const setMainImageOpen = ref(false)
const isImageInfoOpen = ref(false)
const imageToSetAsMain = ref(null)
const openedImage = ref(null)
const imageGrid = ref(null)
const isDeletingImage = ref(false)

const props = defineProps({
  accommodation: Object,
  images: Array,
})

const { deleteImages } = useDeleteImages()
const { displayNotification } = useToastNotifications()
const { imagesLoading, isImagesError } = useGetImages()
const { studioPlusCampaign, isExpiredSubscription } = useStudioPlusCampaign(selectedAccommodationId)
const { campaignsData } = useCampaign(selectedAccommodationId)

const canUploadImage = computed(() => {
  if (studioPlusCampaign.value && !isExpiredSubscription.value) return true
  if (campaignsData.value?.status === 'ACTIVE' || campaignsData.value?.status === 'PAUSED') return true
  else return false
})
const openImageInfoModal = () => {
  isImageInfoOpen.value = true
}
const closeImageInfoModal = () => {
  isImageInfoOpen.value = false
}

const openSetMainImageModal = imagePath => {
  appAnalytics.track(appAnalytics.events.PD_OPEN_SET_MAIN_IMAGE, {
    item_id: selectedAccommodationId.value,
  })
  setMainImageOpen.value = true
  imageToSetAsMain.value = imagePath
}
const closeSetMainImageModal = () => {
  setMainImageOpen.value = false
  imageToSetAsMain.value = null
}

const closeAddImageModal = () => {
  addImageModalOpen.value = false
  if (isUploading.value) {
    window.scrollTo({
      behavior: 'smooth',
      top: imageGrid.value?.getBoundingClientRect().bottom,
    })
  }
}
const openAddImageModal = () => {
  addImageModalOpen.value = true
}
const toggleImageGallery = selectedImage => {
  openedImage.value = selectedImage
  imageGalleryOpen.value = !imageGalleryOpen.value
}

const toggleTipsModal = () => {
  tipsModalOpen.value = !tipsModalOpen.value
}
const openDeleteModal = imageId => {
  if (imageId) {
    setDeleteItemIds([imageId])
  } else {
    setDeleteItemIds([...selectedImageIds.value])
  }
  deleteModalOpen.value = true
}
const closeDeleteModal = () => {
  deleteModalOpen.value = false
  resetDeleteItemIds()
}

const deleteImagesFn = () => {
  isDeletingImage.value = true
  const allItems = props.images.filter(item => deleteItemIds.value.includes(item.imageId))

  const itemsToDelete = allItems.map(item => {
    return {
      image_id: item.imageId,
      active: false,
      reason: 'outdated',
    }
  })

  deleteImages(
    {
      accommodationId: selectedAccommodationId.value,
      body: { updated: itemsToDelete },
    },
    {
      onSuccess: data => {
        if (data.status === 200) {
          displayNotification({
            message:
              itemsToDelete.length > 1
                ? 'app_images_successfully_x_images_deleted.zero'
                : 'app_images_successfully_x_images_deleted.one',
            isTranslationKey: true,
            translationParams: itemsToDelete.length > 1 ? { count: data.data.updated.length } : false,
            isHtml: false,
            type: 'success',
          })
          isDeletingImage.value = false
          closeDeleteModal()
          resetSelectedIds()
        }
      },
      onError: () => {
        displayNotification({
          message: 'something_went_wrong',
          isTranslationKey: true,
          isHtml: false,
          type: 'error',
        })
        isDeletingImage.value = false
        closeDeleteModal()
      },
    }
  )
}
</script>
